import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import Button from "../../../components/essentials/button/Button";
import AcedInput from "../../../components/essentials/input/text/AcedInput";
import LoadingSpinner from "../../../components/essentials/loading-spin/LoadingSpinner";
import { addSuccessMessage } from "../../../core/errors/errorSlice";
import { loginAction } from "../../../core/auth/authSlice";
import { PasswordApiRoutes } from "../../../shared/api-routes/auth/password-api.routes";
import { PASSWORD_PATTERN } from "../../../shared/const";
import http from "../../../shared/utils/http";
import { hashString } from "../../../shared/utils/util";
import "./SetPassword.scss";

const SetPassword: React.FC<{ context: "set" | "recover" }> = ({ context }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	// null -> token is being validated
	// true/false -> token validity
	const [isTokenValid, setTokenValidity] = useState<boolean | null>(null);

	// validate the passwordToken
	useEffect(() => {
		if (!token) {
			setTokenValidity(false);
			return;
		}
		http
			.get(PasswordApiRoutes.VALIDATE_RESET_TOKEN(token), {
				suppressErrorToast: true,
			} as any)
			.then(() => setTokenValidity(true))
			.catch(() => {
				setTokenValidity(false);
			});
	}, [token]);

	const {
		control,
		handleSubmit,
		formState: { errors, isDirty },
		register,
	} = useForm({
		mode: "onChange",
		defaultValues: {
			password: "",
			passwordConfirm: "",
		},
	});

	const validatePwd: (arg0: string, arg1: string) => string | undefined = (
		pwd: string,
		controlValue: string,
	) => {
		return controlValue === pwd
			? undefined
			: t("signUp.passwordSection.errors.mismatch");
	};

	const appDispatch = useAppDispatch();
	const onSubmit = (data: any) => {
		if (!token) return;

		const requestPath = (() => {
			switch (context) {
				case "set":
					return PasswordApiRoutes.CREATE_NEW_PASSWORD(token);
				case "recover":
					return PasswordApiRoutes.RESET_PASSWORD(token);
			}
		})();

		hashString(data.password).then((password) => {
			const requestBody = {
				password,
			};

			http
				.post(requestPath, requestBody, {
					headers: {
						Authorization: "Bearer " + token,
					},
				})
				.then((response) => {
					if (context === "set") {
						// Auto-login for signup flow
						appDispatch(
							addSuccessMessage({
								title: "Profile successfully created!",
							}),
						);
						appDispatch(loginAction(() => navigate("/board")));
					} else {
						// For password recovery, redirect to login
						appDispatch(
							addSuccessMessage({
								title: "Password reset successfully",
							}),
						);
						navigate("/login");
					}
				})
				.catch((error) => console.log(error));
		});
	};
	const selectPage = () => {
		switch (isTokenValid) {
			case null:
				return (
					<>
						<LoadingSpinner />
					</>
				);
			case false:
				return (
					<div className="sign-up-confirmation__caption set-password">
						<p className="sign-up-confirmation__caption-title">
							The link you followed is not valid, or expired.
						</p>
						<p>
							<span
								onClick={() => navigate("/sign-up/password-recovery/email")}
							>
								Click here
							</span>{" "}
							to reset your password
						</p>
					</div>
				);
			case true:
				return (
					<div className="set-password">
						<p className="set-password__caption">
							{(() => {
								switch (context) {
									case "set":
										return t("signUp.passwordSection.title");
									case "recover":
										return t("signUp.recoverPassword.passwordTitle");
								}
							})()}
						</p>

						<form
							id="set-password-form"
							className="set-password__form"
							onSubmit={handleSubmit(onSubmit)}
						>
							<AcedInput
								placeholder={t("signUp.passwordSection.inputPlaceHolder")}
								control={control}
								type="password"
								name="password"
								rules={{
									required: t("signUp.passwordSection.errors.required"),
									pattern: {
										value: PASSWORD_PATTERN,
										message: t("signUp.passwordSection.errors.pattern"),
									},
								}}
							/>

							<AcedInput
								placeholder={t(
									"signUp.passwordSection.cnfirmPasswordPlaceholder",
								)}
								control={control}
								type="password"
								name="passwordConfirm"
								rules={{
									validate: (value) =>
										validatePwd(control._formValues.password, value),
								}}
							/>

							{context === "recover" && (
								<Button className="btn btn-primary ">
									{t("common.buttons.confirm")}
								</Button>
							)}
						</form>
					</div>
				);
		}
	};

	return selectPage();
};

export default SetPassword;
